import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  ApiSuccess,
  CompetitorOfferConsultantOptionsResponse,
  CompetitorOfferDetail,
  CompetitorOfferQueueQuery,
  CompetitorOfferQueueResponse,
  CompetitorOfferStatus,
  CompetitorOfferWorkflowResponse,
  DecideCompetitorOfferPayload,
  DecideCompetitorOfferResponse,
  RevealCompetitorOfferPasswordResponse,
} from '@alamo/types';

import { apiClient } from '../../client';
import { queryKeys } from '../../query-keys';

const BASE = '/admin/competitor-offers';

function useInvalidateCompetitorOffers() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.adminCompetitorOffers.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.competitorOffers.all });
  };
}

export function useCompetitorOfferQueue(query: CompetitorOfferQueueQuery = {}) {
  return useQuery({
    queryKey: queryKeys.adminCompetitorOffers.queue(query as Record<string, unknown>),
    queryFn: async (): Promise<CompetitorOfferQueueResponse> => {
      const response = await apiClient.get<ApiSuccess<CompetitorOfferQueueResponse>>(
        `${BASE}/queue`,
        { params: query },
      );
      return response.data.data;
    },
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

/**
 * Solicitações do Álamo Cobre cuja próxima ação é do analista: `pending` ainda
 * não foi assumida e `under_analysis` está assumida sem decisão. São os dois
 * status com SLA correndo — `correction_requested` pausa o SLA porque a bola
 * está com o consultor, e os demais são desfecho.
 */
const ANALYST_PENDING_STATUSES = ['pending', 'under_analysis'] as const satisfies readonly [
  CompetitorOfferStatus,
  CompetitorOfferStatus,
];

const QUEUE_COUNT_STALE_MS = 15_000;
const QUEUE_COUNT_REFETCH_MS = 30_000;

/**
 * Contagem do badge do Álamo Cobre no menu admin.
 *
 * A fila não tem endpoint de contagem próprio, então cada status pendente é
 * consultado com `pageSize=1` e só o `count` da resposta é usado. `data` é
 * `undefined` enquanto qualquer um dos status não respondeu, para o badge não
 * piscar um total parcial.
 */
export function useCompetitorOfferQueueCount(options?: { enabled?: boolean }) {
  const enabled = options?.enabled ?? true;
  return useQueries({
    queries: ANALYST_PENDING_STATUSES.map((status) => ({
      queryKey: queryKeys.adminCompetitorOffers.queueCount(status),
      queryFn: async (): Promise<number> => {
        const response = await apiClient.get<ApiSuccess<CompetitorOfferQueueResponse>>(
          `${BASE}/queue`,
          { params: { status, page: 1, pageSize: 1 } satisfies CompetitorOfferQueueQuery },
        );
        return response.data.data.count;
      },
      enabled,
      staleTime: QUEUE_COUNT_STALE_MS,
      refetchInterval: QUEUE_COUNT_REFETCH_MS,
    })),
    combine: (results) => ({
      data: results.every((result) => result.data !== undefined)
        ? results.reduce((total, result) => total + (result.data ?? 0), 0)
        : undefined,
      isFetching: results.some((result) => result.isFetching),
      isError: results.some((result) => result.isError),
    }),
  });
}

export async function fetchCompetitorOfferConsultants(
  search?: string,
): Promise<CompetitorOfferConsultantOptionsResponse> {
  const response = await apiClient.get<ApiSuccess<CompetitorOfferConsultantOptionsResponse>>(
    `${BASE}/consultants/options`,
    { params: search ? { search } : undefined },
  );
  return response.data.data;
}

export function useCompetitorOfferConsultants(search?: string) {
  return useQuery({
    queryKey: [...queryKeys.adminCompetitorOffers.all, 'consultants', search ?? ''],
    queryFn: () => fetchCompetitorOfferConsultants(search),
    staleTime: 60_000,
  });
}

export function useCompetitorOfferDetail(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.adminCompetitorOffers.detail(id ?? ''),
    enabled: Boolean(id),
    queryFn: async (): Promise<CompetitorOfferDetail> => {
      if (!id) throw new Error('id is required');
      const response = await apiClient.get<ApiSuccess<CompetitorOfferDetail>>(`${BASE}/${id}`);
      return response.data.data;
    },
  });
}

export function useStartCompetitorOfferAnalysis() {
  const invalidate = useInvalidateCompetitorOffers();
  return useMutation({
    retry: false,
    mutationFn: async (id: string): Promise<CompetitorOfferWorkflowResponse> => {
      const response = await apiClient.post<ApiSuccess<CompetitorOfferWorkflowResponse>>(
        `${BASE}/${id}/start-analysis`,
      );
      return response.data.data;
    },
    onSuccess: invalidate,
  });
}

export function useRequestCompetitorOfferCorrection() {
  const invalidate = useInvalidateCompetitorOffers();
  return useMutation({
    retry: false,
    mutationFn: async (input: { id: string; reason: string }) => {
      const response = await apiClient.post<ApiSuccess<CompetitorOfferWorkflowResponse>>(
        `${BASE}/${input.id}/request-correction`,
        { reason: input.reason },
      );
      return response.data.data;
    },
    onSuccess: invalidate,
  });
}

export function useAssignCompetitorOfferAnalyst() {
  const invalidate = useInvalidateCompetitorOffers();
  return useMutation({
    retry: false,
    mutationFn: async (input: { id: string; analystId: string }) => {
      const response = await apiClient.post<ApiSuccess<CompetitorOfferWorkflowResponse>>(
        `${BASE}/${input.id}/assign`,
        { analystId: input.analystId },
      );
      return response.data.data;
    },
    onSuccess: invalidate,
  });
}

export function useRevealCompetitorOfferPassword() {
  return useMutation({
    retry: false,
    mutationFn: async (id: string): Promise<RevealCompetitorOfferPasswordResponse> => {
      const response = await apiClient.post<ApiSuccess<RevealCompetitorOfferPasswordResponse>>(
        `${BASE}/${id}/reveal-pdf-password`,
      );
      return response.data.data;
    },
  });
}

export function useDecideCompetitorOffer() {
  const invalidate = useInvalidateCompetitorOffers();
  return useMutation({
    retry: false,
    mutationFn: async (input: {
      id: string;
      payload: DecideCompetitorOfferPayload;
    }): Promise<DecideCompetitorOfferResponse> => {
      const response = await apiClient.post<ApiSuccess<DecideCompetitorOfferResponse>>(
        `${BASE}/${input.id}/decide`,
        input.payload,
      );
      return response.data.data;
    },
    onSuccess: invalidate,
  });
}
